/**
 * Tracking utility functions for Google Tag Manager
 */

/**
 * Tracks clicks on contact links (phone and email)
 * @param type - Type of contact (phone or email)
 * @param value - The actual phone number or email address
 * @param location - Optional location/context where the click occurred
 */
export function trackContactClick(type: 'phone' | 'email', value: string, location?: string): void {
  // console.log('trackContactClick called with:', { type, value, location });
  if (!process.client) return;

  try {
    // @ts-ignore
    const dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: 'contactClick',
      eventModel: {
        contact_type: type,
        contact_value: value,
        contact_location: location || window.location.href,
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Error tracking contact click:', error);
  }
}

/**
 * Tracks clicks on CompCard components (News, Articles, References)
 * @param category - Card category (e.g. 'News', 'Referenzen', 'Projekte')
 * @param title - Card title/text
 * @param url - Destination URL
 * @param subtags - Optional subtags array
 * @param position - Optional position/index of the card in the list
 * GTM-Trigger: CE - Card Click - News mit Ereignisname "cardClick"
 *    Auslösen bei: DL - Card Category enthält Neuigkeiten
 * GTM-Tags: GA4 - Event Card Click - News mit Ereignisname "card_click_news"
 * Testing: https://www.geigergruppe.com/de-de/neuigkeiten/
 * GTM-Trigger: CE - Card Click - Referenzen mit Ereignisname "cardClick"
 *    Auslösen bei: DL - Card Category enthält Referenzen
 * GTM-Tags: GA4 - Event Card Click - Referenzen mit Ereignisname "card_click_referenzen"
 * Testing: https://www.geigergruppe.com/de-de/referenzen/
 */
export function trackCardClick(
  category: string,
  title: string,
  url: string,
  subtags?: string[],
  position?: number
): void {
  // console.log('trackCardClick called with:', { category, title, url, subtags, position });
  if (!process.client) return;

  try {
    // @ts-ignore
    const dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: 'cardClick',
      eventModel: {
        card_category: category || 'unknown',
        card_title: title,
        card_url: url,
        card_subtags: subtags && subtags.length > 0 ? subtags.join(', ') : undefined,
        card_position: position !== undefined ? position + 1 : undefined, // +1 for human-readable position
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Error tracking card click:', error);
  }
}

/**
 * Tracks clicks on TeaserItem components in TmGrid
 * @param headline - Teaser headline/title
 * @param url - Destination URL
 * GTM-Trigger: CE - Teaser Click mit Ereignisname "teaserClick"
 * GTM-Tags:    GA4 - Event Teaser Click mit Ereignisname "teaser_click"
 * Testing: https://www.geigergruppe.com/de-de/articles/neuigkeit-erste-mieter-im-wohnabauprojekt-daheim-/
 * Ggf. für die Ereignisparameter noch Variablen anlegen - analog zu cardClick):
    DL - Teaser Headline / Typ: Datenebenen-Variable
    Name: eventModel.teaser_headline
    DL - Teaser URL / Typ: Datenebenen-Variable
    Name: eventModel.teaser_url
    DL - Teaser Page URL / Typ: Datenebenen-Variable
    Name: eventModel.page_url
    Dann im GA4-Tag einfach {{DL - Teaser Headline}} statt den vollen Pfad verwenden. Das ist auch konsistent mit deinem bestehenden Setup für trackCardClick, wo du ja auch DataLayer-Variablen nutzt (siehe die Kommentare im Code: "DL - Card Category").
*/
export function trackTeaserClick(headline: string, url: string): void {
  // console.log('trackTeaserClick called with:', { headline, url });
  if (!process.client) return;

  try {
    // @ts-ignore
    const dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: 'teaserClick',
      eventModel: {
        teaser_headline: headline,
        teaser_url: url,
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Error tracking teaser click:', error);
  }
}

/**
 * Tracks clicks on main navigation links in the Header
 * @param label - Navigation link label/text
 * @param url - Destination URL
 * @param location - Navigation location ('main-nav', 'inline-nav', 'mobile-menu', 'service-menu', 'contact', 'language', 'footer-nav')
 * @param category - Optional category for service menu items
 * GTM-Setup:
 * 1. DataLayer-Variablen in GTM anlegen:
 *    DL - Nav Label / Typ: Datenebenen-Variable / Name: eventModel.nav_label
 *    DL - Nav URL / Typ: Datenebenen-Variable / Name: eventModel.nav_url
 *    DL - Nav Location / Typ: Datenebenen-Variable / Name: eventModel.nav_location
 *    DL - Nav Category / Typ: Datenebenen-Variable / Name: eventModel.nav_category
 * 2. GTM-Trigger: CE - Navigation Click mit Ereignisname "navigationClick"
 * 3. GTM-Tag: GA4 - Event Navigation Click mit Ereignisname "navigation_click" - Variablen als Ereignisparameter hinzufügen!
 */
export function trackNavigationClick(
  label: string,
  url: string,
  location: 'main-nav' | 'inline-nav' | 'mobile-menu' | 'service-menu' | 'contact' | 'language' | 'footer-nav',
  category?: string
): void {
  // console.log('trackNavigationClick called with:', { label, url, location, category });
  if (!process.client) return;

  try {
    // @ts-ignore
    const dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: 'navigationClick',
      eventModel: {
        nav_label: label,
        nav_url: url,
        nav_location: location,
        nav_category: category ? category.replace(/<br\s*\/?>/gi, ' ').trim() : '',
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Error tracking navigation click:', error);
  }
}

/**
 * Tracks clicks on primary CTA buttons (variant="primary") in content modules
 * @param label - Button label/text
 * @param url - Destination URL
 * @param module - Module name where the button is located ('CmStage', 'TmHighlight', 'TmStandard', etc.)
 * GTM-Setup:
 * 1. DataLayer-Variablen in GTM anlegen:
 *    DL - Button Label / Typ: Datenebenen-Variable / Name: eventModel.button_label
 *    DL - Button URL / Typ: Datenebenen-Variable / Name: eventModel.button_url
 *    DL - Button Module / Typ: Datenebenen-Variable / Name: eventModel.button_module
 *    DL - Button Page URL / Typ: Datenebenen-Variable / Name: eventModel.page_url
 * 2. GTM-Trigger: CE - Button Click mit Ereignisname "buttonClick"
 * 3. GTM-Tag: GA4 - Event Button Click mit Ereignisname "button_click" - Variablen als Ereignisparameter hinzufügen!
 */
export function trackButtonClick(label: string, url: string, module: string): void {
  // console.log('trackButtonClick called with:', { label, url, module });
  if (!process.client) return;

  try {
    // @ts-ignore
    const dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: 'buttonClick',
      eventModel: {
        button_label: label,
        button_url: url || '',
        button_module: module,
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Error tracking button click:', error);
  }
}

/**
 * Tracks clicks on links in content modules (e.g. CmTextMedia, CmTextImageHighlight)
 * @param label - Link label/text
 * @param url - Destination URL
 * @param module - Module name where the link is located ('CmTextMedia', 'CmTextImageHighlight', etc.)
 * GTM-Setup:
 * 1. DataLayer-Variablen in GTM anlegen:
 *    DL - Link Label / Typ: Datenebenen-Variable / Name: eventModel.link_label
 *    DL - Link URL / Typ: Datenebenen-Variable / Name: eventModel.link_url
 *    DL - Link Module / Typ: Datenebenen-Variable / Name: eventModel.link_module
 *    DL - Link Page URL / Typ: Datenebenen-Variable / Name: eventModel.page_url
 * 2. GTM-Trigger: CE - Link Click mit Ereignisname "linkClick"
 * 3. GTM-Tag: GA4 - Event Link Click mit Ereignisname "link_click" - Variablen als Ereignisparameter hinzufügen!
 */
export function trackLinkClick(label: string, url: string, module: string): void {
  // console.log('trackLinkClick called with:', { label, url, module });
  if (!process.client) return;

  try {
    // @ts-ignore
    const dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: 'linkClick',
      eventModel: {
        link_label: label,
        link_url: url || '',
        link_module: module,
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Error tracking link click:', error);
  }
}
